export const SITE = {
  name: "Scryla",
  person: "Timothy Ng",
  url: "https://www.scryla.co.uk",
  email: "info@scryla.co.uk",
  linkedin: "https://www.linkedin.com/in/tkcng/",
  linkedinLabel: "linkedin.com/in/tkcng",
  location: "Manchester, UK",
  // Set to a real number (E.164 in `phone`, display form in `phoneLabel`) to show the phone row.
  phone: "",
  phoneLabel: "",
  officeHours:
    "Monday to Friday, 9am to 6pm UK time. UK mornings overlap with South East Asia afternoons (GMT+8) and UK afternoons with US East Coast mornings.",

  logo: "/images/scryla-logo.png",
  logoWidth: 1250,
  logoHeight: 500,
  // Stable, crawlable portrait for the Person entity. Kept in /public so the
  // URL does not change with every build hash.
  portrait: "/images/timothy-ng.jpg",
  portraitSize: 224,
  gaId: "G-TH734MLPR0",
  clarityId: "84pyownbta",
} as const;

// Primary navigation is deliberately tight for a board and founder audience.
// Client feedback sits under case studies and About Tim rather than in the top
// bar, and LinkedIn lives in the footer and on About Tim.
export const NAV = [
  { to: "/", label: "Home" },
  { to: "/what-i-do", label: "Fractional CTO" },
  { to: "/board-advisory", label: "Board and advisory" },
  { to: "/case-studies", label: "Case studies" },
  { to: "/who-i-am", label: "About Tim" },
  { to: "/articles", label: "Insights" },
] as const;

/** One label and one reassurance line for the primary call to action, site-wide. */
export const CTA = {
  label: "Tell me what you're weighing up",
  short: "Tell me what you're weighing up",
  reassurance: "30 to 45 minutes. No pitch. If I am not the right person, I will tell you.",
} as const;

export type Testimonial = { quote: string; name: string; role: string };

export const TESTIMONIALS = {
  steveKelly: {
    quote:
      "Engaging Tim as an advisor was one of the best decisions we made for our company. Tim brings a wealth of experience, knowledge and a fresh perspective to the boardroom. He has helped us to make better strategic decisions, improved our governance and brought valuable connections to the table. He effectively communicated complex technical concepts and provided continued support.",
    name: "Steve Kelly",
    role: "Founder, EntireAI",
  },
  steveKellyShort: {
    quote:
      "Engaging Tim as an advisor was one of the best decisions we made for our company. Tim brings a wealth of experience, knowledge and a fresh perspective to the boardroom. He has helped us to make better strategic decisions, improved our governance and brought valuable connections to the table.",
    name: "Steve Kelly",
    role: "Founder, EntireAI",
  },
  richardGregory: {
    quote:
      "Tim is results driven with all his endeavours, focused on the delivery of on-time solutions to the benefit of the business, an assured change manager who incorporates business change and technology to maximum effect. Tim also excels at closing the communication gap between less technical senior managers and the hands-on development team.",
    name: "Richard Gregory",
    role: "NED and advisor to multiple tech companies and agencies",
  },
  aaronBiggs: {
    quote:
      "I highly recommend Tim Ng and Scryla Consultancy to any company seeking technical analysis or development process strategic advice. Tim's expertise and professionalism are second to none, and his contributions to Tahdah Verified Ltd have been invaluable.",
    name: "Aaron Biggs",
    role: "COO, Tahdah Verified Ltd",
  },
  danielReilly: {
    quote:
      "Aside from his knowledge of big data and the technologies and methodologies that surround it, Tim has been a great sounding board for myself and my business partner with what we are building at Ruler Analytics and Epic New Media.",
    name: "Daniel Reilly",
    role: "Founder, Ruler Analytics",
  },
  danielReillyShort: {
    quote:
      "Tim has been a great sounding board for myself and my business partner with what we are building.",
    name: "Daniel Reilly",
    role: "Founder, Ruler Analytics",
  },
  milanShah: {
    quote:
      "Working with Tim and Scryla was a great experience. The guidance and consultation helped us plan our project and defined our scope. Through a thorough report, Tim helped us understand the phases of work with our outsourced development team. We would recommend any start up looking for a steer to work with Tim and Scryla.",
    name: "Milan Shah",
    role: "Director, 3PP",
  },
  philYoung: {
    quote:
      "I have worked with Tim Ng for over 10 years in two different companies. Where he excels is taking the weird, half-baked ideas out of my head, and building a system that makes them work, be it a website, infrastructure or CRM.",
    name: "Phil Young",
    role: "Former Managing Director, threesixty services LLP",
  },
  damianDavies: {
    quote:
      "As we grow, we know we need our systems to flex with us. Working with Tim is allowing us to realise the growth potential of the business without some of the pains we hear others experience.",
    name: "Damian Davies",
    role: "Owner, The Timebank",
  },
} satisfies Record<string, Testimonial>;

/** Short pull quotes for the home page credibility section. */
export type TrustedQuote = Testimonial & { title: string; organisation: string };

export const TRUSTED_BY: TrustedQuote[] = [
  {
    quote:
      "Engaging Tim as an advisor was one of the best decisions we made for our company. He brings a wealth of experience and a fresh perspective to the boardroom.",
    name: TESTIMONIALS.steveKelly.name,
    role: TESTIMONIALS.steveKelly.role,
    title: "Founder",
    organisation: "EntireAI",
  },
  {
    quote:
      "Tim excels at closing the communication gap between less technical senior managers and the hands-on development team.",
    name: TESTIMONIALS.richardGregory.name,
    role: TESTIMONIALS.richardGregory.role,
    title: "Non-executive director and advisor",
    organisation: "Multiple tech companies and agencies",
  },
  {
    quote: "We would recommend any start up looking for a steer to work with Tim and Scryla.",
    name: TESTIMONIALS.milanShah.name,
    role: TESTIMONIALS.milanShah.role,
    title: "Director",
    organisation: "3PP",
  },
];

export const CLIENT_LOGOS = [
  "ClickTech",
  "Entire AI",
  "Grace Media",
  "NowPatient",
  "Mycardium",
  "Ocuwell",
  "Summit",
];

export const PROJECTS = [
  "Designing and developing a digital health care platform from inception to launch, the only platform of its kind which combines digital remote consultations with doctor and internet pharmacy services.",
  "Working with a corporate client and their senior management team to grow a business from seed to £3 million revenue in less than three years.",
  "Working with private equity houses and private funds to raise capital investment for growth, series A and B.",
  "Coaching and mentoring CTOs, COOs and their teams.",
  "Providing strategic technology and information governance input in digital health, including current DSPT work, GDPR and ISO 27001, alongside earlier experience with the NHS Information Governance Toolkit it replaced.",
  "Implementing a cost reduction programme which reduced operational costs by 70%.",
];

export const EXPERIENCE = [
  {
    role: "Co-founder and Director",
    org: "Novidian",
    sector: "Technology consultancy",
    period: "2024, present",
  },
  {
    role: "Founder and Director",
    org: "Scryla Consultancy",
    sector: "Technology consultancy",
    period: "2014, present",
  },
  {
    role: "Strategic Advisor",
    org: "RavenTrack",
    sector: "iGaming and affiliate analytics",
    period: "2024, present",
  },
  { role: "CTO Advisor", org: "Infohealth", sector: "Digital health", period: "2020, present" },
  {
    role: "Advisor and CTO",
    org: "ClickTech (Adzooma)",
    sector: "Marketing SaaS",
    period: "2020, 2025",
  },
  {
    role: "CTO Advisor",
    org: "Grace Media",
    sector: "iGaming and affiliate media",
    period: "2024",
  },
  { role: "Strategic Advisor", org: "Ocuwell", sector: "Digital health", period: "2023, 2024" },
  {
    role: "Chief Operating Officer",
    org: "Now Healthcare Group",
    sector: "Digital health",
    period: "2015, 2020",
  },
  {
    role: "Chief Technology Officer",
    org: "Property Moose",
    sector: "Property fintech",
    period: "2014, 2015",
  },
  {
    role: "Operations Director",
    org: "threesixty services",
    sector: "Financial services",
    period: "2009, 2014",
  },
];

export const ARTICLES = [
  {
    date: "03/02/2025",
    title: "Why your startup needs an on-demand CTO to grow",
    summary:
      "How strategic technical leadership helps you scale and avoid technical pitfalls, at a time and cost that suits you.",
  },
  {
    date: "16/12/2024",
    title: "What should founders do now to prepare a startup for growth?",
    summary:
      "Practical strategies for driving efficiency and innovation, and doing more with less in challenging times.",
  },
  {
    date: "16/09/2024",
    title: "The cost of not having a CTO, and why founders cannot afford to get it wrong",
    summary:
      "Costly mistakes, missed opportunities and scalability problems that follow from a gap in technical leadership.",
  },
  {
    date: "26/08/2024",
    title: "A guide to building scalable tech infrastructure for startups and SMEs",
    summary: "For board members thinking about infrastructure and how to prepare for growth.",
  },
  {
    date: "19/08/2024",
    title: "A guide for investors considering investing in tech startups",
    summary: "Key metrics and red flags to check before you pass the cheque.",
  },
  {
    date: "05/08/2024",
    title: "Tech audit 101: uncovering invisible costs that impact ROI",
    summary:
      "Technical debt, vendor management, compliance and subscriptions, and how they quietly erode profitability.",
  },
];

export const SERVICES = [
  {
    title: "Business audits",
    short:
      "An honest read of where your technology stands, written so you and your board can act on it.",
    long: "Where your technology, team, spend and risk actually stand today. I look at the architecture, the delivery process, the suppliers and the cost base, then write it up in language a board can act on rather than a technical report nobody reads.",
  },
  {
    title: "Strategy development",
    short: "A plan with sequence, cost and trade-offs named, not a wish list.",
    long: "A technology plan that follows from the business plan. Sequence, cost and trade-offs named, so you know what you are committing to before you commit, and so your team knows what they are building towards.",
  },
  {
    title: "Coaching and mentoring",
    short:
      "For CTOs, COOs and their teams, and for founders growing into the technical side of the job.",
    long: "For CTOs, COOs and their teams. Also for founders who want to hold their own in a technical conversation, whether that is with a development team, an acquirer or an investor.",
  },
  {
    title: "Project delivery",
    short: "Steering projects, in-house or outsourced, when a programme needs a steady hand on it.",
    long: "Steering delivery when a programme needs it. That includes holding outsourced development teams to what they promised, and giving you a clear read on progress that is not filtered through the people doing the work.",
  },
  {
    title: "Support alongside your existing CTO",
    short:
      "Temporary weight off an in-post CTO: a project, a restructure, or a second opinion to test thinking against.",
    long: "Plenty of my work sits next to a CTO who is already doing the job well and simply has more on than one person can carry. That might be owning a specific project or programme while they hold the rest, working through a team restructure or a scale-up with them, or being the person they can think out loud with before a decision goes to the board. I work to their agenda, not around them, and the arrangement ends when the pressure does.",
  },
];
